/**
 * Produtos com variações — helpers PUROS (sem I/O).
 *
 * ARQUITETURA: a variação É uma linha em `products`, filha de outra linha em
 * `products` (`parent_product_id`). NÃO existe tabela separada. Consequência
 * boa: tudo que aponta para um produto (`quote_items.product_id`,
 * `service_order_items.product_id`, `stock_movements.product_id`) já aponta
 * para a VARIAÇÃO certa — nada de `variant_id`, e `toDeductionLines`
 * (src/lib/stock.ts) consolida por `product_id`, então "2 P + 1 M" dá duas
 * baixas distintas de graça.
 *
 * Consequência a pagar: as consultas planas de `products` trazem pais e filhas
 * misturados. Estas funções são o remédio — separam raízes de filhas e derivam
 * o que o pai exibe (contagem, faixa de preço, saldo somado).
 *
 * Regras de produto:
 *  - "Tem variações" = EXISTE FILHA (nunca uma flag booleana que possa divergir).
 *  - Pai com variações não é vendável nem estocável; quem vende/estoca é a filha.
 *  - Um nível só, e só em produto físico (o banco impõe — ver a migration).
 */
import { invalidVariantCostMessage, parseCostInput } from '@/lib/cost';
import { parseBRLInput } from '@/lib/currencyInput';

/**
 * O mínimo que estes helpers precisam de um produto — aceita tanto a linha crua
 * do banco quanto os tipos concretos da UI (`Product`, `CatalogItem`).
 *
 * Sem index signature de propósito: `[key: string]: unknown` faria interfaces
 * concretas deixarem de ser atribuíveis a este tipo, e todo chamador precisaria
 * de cast.
 */
export interface VariantAwareProduct {
  id: string;
  name?: string | null;
  price?: number | null;
  type?: string | null;
  parent_product_id?: string | null;
  variant_name?: string | null;
  track_stock?: boolean | null;
  stock_quantity?: number | null;
}

/** Uma variação é qualquer produto que aponta para um pai. */
export function isVariant(product: Pick<VariantAwareProduct, 'parent_product_id'>): boolean {
  return product?.parent_product_id != null && String(product.parent_product_id).length > 0;
}

/** Uma raiz é qualquer produto sem pai (produto simples OU pai de variações). */
export function isRoot(product: Pick<VariantAwareProduct, 'parent_product_id'>): boolean {
  return !isVariant(product);
}

/**
 * Agrupa as filhas por pai. Filhas de um pai ausente da lista (fora do escopo
 * atual) são ignoradas de propósito — nunca viram raízes órfãs na tela.
 */
export function groupVariants<T extends VariantAwareProduct>(products: T[]): Map<string, T[]> {
  const byParent = new Map<string, T[]>();
  for (const p of products ?? []) {
    if (!isVariant(p)) continue;
    const parentId = String(p.parent_product_id);
    const list = byParent.get(parentId);
    if (list) list.push(p);
    else byParent.set(parentId, [p]);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) =>
      String(a.variant_name ?? '').localeCompare(String(b.variant_name ?? ''), 'pt-BR', { numeric: true }),
    );
  }
  return byParent;
}

/** Ids dos produtos que TÊM ao menos uma variação (= não são vendáveis/estocáveis). */
export function parentIdsWithVariants(products: VariantAwareProduct[]): Set<string> {
  const ids = new Set<string>();
  for (const p of products ?? []) {
    if (isVariant(p)) ids.add(String(p.parent_product_id));
  }
  return ids;
}

/**
 * Lista hierárquica: só as raízes, cada uma com suas filhas anexadas. É o que a
 * tela do catálogo (e o seletor, e o XLSX) consomem no lugar da lista plana.
 */
export interface VariantGroup<T extends VariantAwareProduct> {
  parent: T;
  variants: T[];
  /** Atalho: o pai tem variações? (= variants.length > 0) */
  hasVariants: boolean;
}

export function buildVariantGroups<T extends VariantAwareProduct>(products: T[]): VariantGroup<T>[] {
  const byParent = groupVariants(products);
  return (products ?? [])
    .filter((p) => isRoot(p))
    .map((parent) => {
      const variants = byParent.get(String(parent.id)) ?? [];
      return { parent, variants, hasVariants: variants.length > 0 };
    });
}

/**
 * Nome legível de uma variação: "Camiseta — P". O `quote_items.name` guarda
 * este texto, então o orçamento/PDF mostra a variação exata sem coluna nova.
 */
export function variantDisplayName(
  parentName: string | null | undefined,
  variantName: string | null | undefined,
): string {
  const base = String(parentName ?? '').trim();
  const label = String(variantName ?? '').trim();
  if (!label) return base;
  if (!base) return label;
  return `${base} — ${label}`;
}

/** Faixa de preço das variações. `null` quando não há variação. */
export function priceRange(variants: VariantAwareProduct[]): { min: number; max: number } | null {
  const prices = (variants ?? []).map((v) => Number(v.price ?? 0)).filter((n) => Number.isFinite(n));
  if (prices.length === 0) return null;
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

/** Saldo somado das variações RASTREADAS (o pai não tem saldo próprio). */
export function totalVariantStock(variants: VariantAwareProduct[]): number {
  return (variants ?? [])
    .filter((v) => v.track_stock === true)
    .reduce((sum, v) => sum + Number(v.stock_quantity ?? 0), 0);
}

/** Alguma variação rastreia estoque? (decide se o pai mostra o saldo somado) */
export function hasTrackedVariant(variants: VariantAwareProduct[]): boolean {
  return (variants ?? []).some((v) => v.track_stock === true);
}

/**
 * A unidade REALMENTE estocável: um produto rastreado que não seja um pai com
 * variações. Sem isto, um pai que rastreava estoque antes de ganhar variações
 * apareceria no Estoque em dobro (ele + as filhas).
 */
export function isStockableUnit(
  product: Pick<VariantAwareProduct, 'id' | 'type' | 'track_stock'>,
  parentsWithVariants: Set<string>,
): boolean {
  if (product?.type !== 'product') return false;
  if (product?.track_stock !== true) return false;
  return !parentsWithVariants.has(String(product.id));
}

/**
 * Expande uma lista de ids para incluir as variações dos que forem pais.
 *
 * É O HELPER MAIS IMPORTANTE DO ARQUIVO: `quote_items.product_id` é ON DELETE
 * **NO ACTION** (diferente de `service_order_items`, que é SET NULL), então o
 * cascade que apaga as filhas estoura violação de FK se um `quote_items` ainda
 * apontar para uma filha. Todo unlink/delete precisa passar por aqui.
 */
export function withVariantIds(ids: string[], products: VariantAwareProduct[]): string[] {
  const target = new Set(ids.map(String));
  for (const p of products ?? []) {
    if (isVariant(p) && target.has(String(p.parent_product_id))) target.add(String(p.id));
  }
  return Array.from(target);
}

/** Uma variação em edição no formulário do catálogo (ainda sem id se for nova). */
export interface VariantDraft {
  /** Chave estável da linha no formulário (não é o id do banco). */
  key: string;
  /** Id no banco — ausente enquanto a variação não foi salva. */
  id?: string;
  variant_name: string;
  /** Preço como texto pt-BR ("12,90"), do jeito que o usuário digita. */
  price: string;
  /**
   * Custo como texto pt-BR — OPCIONAL (vazio = não informado). Cada variação tem
   * o próprio custo, pelo mesmo motivo de ter o próprio preço: a filha é a
   * unidade que se compra e se vende.
   */
  cost: string;
  barcode: string;
  /**
   * Código interno/SKU da variação. Mesma regra do `barcode`: quem se compra e
   * se vende é a variação, então é ela que carrega os códigos (o pai é só
   * agrupador). Vazio = não informado.
   */
  internal_code: string;
  /** Saldo atual (só leitura — o saldo só muda por movimento/RPC). */
  stock_quantity?: number;
}

export interface VariantDraftValidation {
  ok: boolean;
  /** Mensagem pronta para o toast (pt-BR), ou null quando está tudo certo. */
  error: string | null;
}

/**
 * Valida os rascunhos de variação do formulário: rótulo obrigatório e único,
 * preço válido. Puro de propósito — é o que os testes cobrem.
 */
export function validateVariantDrafts(drafts: VariantDraft[]): VariantDraftValidation {
  const list = drafts ?? [];
  if (list.length === 0) return { ok: true, error: null };

  const seen = new Set<string>();
  for (const d of list) {
    const label = String(d.variant_name ?? '').trim();
    if (!label) return { ok: false, error: 'Dê um nome a cada variação (ex.: P, M, G).' };
    if (label.length > 80) return { ok: false, error: `O nome da variação "${label}" é longo demais (máx. 80).` };

    const key = label.toLocaleLowerCase('pt-BR');
    if (seen.has(key)) return { ok: false, error: `A variação "${label}" está repetida.` };
    seen.add(key);

    const price = parseVariantPrice(d.price);
    if (price == null || price <= 0) {
      return { ok: false, error: `Informe um preço válido para a variação "${label}".` };
    }

    // Custo é OPCIONAL: só reclama quando o texto digitado não é um custo válido
    // (vazio continua sendo "não informado", que é legítimo).
    if (!parseCostInput(d.cost).ok) {
      return { ok: false, error: invalidVariantCostMessage(label) };
    }
  }

  return { ok: true, error: null };
}

/**
 * Lê um preço digitado em pt-BR ("12,90" · "1.500,00") — null quando inválido.
 *
 * O campo é MASCARADO (`maskBRLInput`), então o texto chega sempre bem formado;
 * o parser de dinheiro é quem sabe ler o milhar. O `parseFloat(raw.replace(',',
 * '.'))` que existia aqui lia "1.500" como **1,5** — mil vezes menos, gravado
 * sem erro nenhum na tela (docs/testes.md §3.17).
 *
 * O contrato de `null` é preservado de propósito: quem chama distingue "não
 * informado / inválido" de um número, e `parseBRLInput` sozinho devolveria 0
 * para texto sem dígito — colapsando as duas coisas.
 */
export function parseVariantPrice(value: string | number | null | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = String(value ?? '').trim();
  if (!/\d/.test(raw)) return null;
  return parseBRLInput(raw);
}

/**
 * Preço que o PAI grava. `products.price` é NOT NULL e o pai não é vendável,
 * então guardamos o MENOR preço entre as variações ("a partir de R$ X") — é o
 * que a faixa de preço já mostra, e mantém qualquer leitura legada sã.
 */
/**
 * NÃO existe `parentCostFromDrafts` de propósito. O preço do pai precisa de um
 * valor porque a coluna é NOT NULL; `cost` é NULLABLE, então o pai de um produto
 * com variações fica com `cost = null` — ele não é comprável nem vendável, e um
 * custo "de mentira" nele viraria capital imobilizado fantasma (contado duas
 * vezes por um futuro relatório de lucro, junto com o das filhas). Quem tem
 * custo é a variação. Ao converter um produto simples em produto com variações,
 * o custo que estava no pai é herdado pela PRIMEIRA variação (ver
 * `addVariantDraft` na CatalogPage), então nada se perde.
 */
export function parentPriceFromDrafts(drafts: VariantDraft[], fallback: number): number {
  const prices = (drafts ?? [])
    .map((d) => parseVariantPrice(d.price))
    .filter((n): n is number => n != null && n > 0);
  if (prices.length === 0) return fallback;
  return Math.min(...prices);
}
